/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Column Personalization
 */
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { Button, Divider, Flex, Popover, Tooltip } from 'antd';
import { AlignHorizontalCenterTwo } from '@icon-park/react';

import SortableList from './SortableList';
import useUpdateUserAttributes from 'hooks/mutate/useUpdateUserAttributes';
import { useAppStoreValues } from 'store/app';
import { prepareSubmitModel } from './utils';

/**
 * Column Personalization Props
 */
interface IProps {
	personalizationKey: string;
	columnMeta: IColumnMeta[];
}

/**
 * Column Personalization
 * @returns {React.FC} ColumnPersonalization
 */
const ColumnPersonalization: React.FC<IProps> = ({ personalizationKey, columnMeta }) => {
	const { user } = useAppStoreValues();
	const { mutate, isPending } = useUpdateUserAttributes();
	const [isOpen, setIsOpen] = useState(false);
	const [listItems, setListItems] = useState<IColumnMeta[]>([]);
	const disableSave = _.isEqual(columnMeta, listItems);

	useEffect(() => {
		setListItems(columnMeta);
	}, [columnMeta]);

	const submitAttributes = (attributes: IColumnMeta[]) => {
		mutate({ id: user?.id, key: personalizationKey, attributes }, { onSuccess: () => setIsOpen(false) });
	};

	const handleReset = () => {
		submitAttributes([]);
	};

	const resetStates = () => {
		setIsOpen(false);
		setListItems(columnMeta);
	};

	const handleSave = () => {
		submitAttributes(prepareSubmitModel(listItems));
	};

	// TODO new keys missing in columnMeta
	const getContent = () => {
		return (
			<Flex vertical data-testid='ag-column-customize-popover'>
				<Divider style={{ margin: 0 }} />
				<SortableList
					items={listItems}
					listStyle={{ maxHeight: '50vh', overflow: 'scroll' }}
					onChange={(newItems) => setListItems(newItems)}
				/>
				<Divider style={{ margin: '10px 0' }} />
				<Flex justify='space-between'>
					<Button type='link' size='small' onClick={handleReset} data-testid='ag-column-customize-reset'>
						{'Reset'}
					</Button>
					<Flex gap={8}>
						<Button type='default' size='small' onClick={resetStates} data-testid='ag-column-customize-cancel'>
							{'Cancel'}
						</Button>
						<Button
							type='primary'
							size='small'
							loading={isPending}
							disabled={disableSave}
							onClick={handleSave}
							data-testid='ag-column-customize-save'
						>
							{'Save'}
						</Button>
					</Flex>
				</Flex>
			</Flex>
		);
	};

	return (
		<Popover
			open={isOpen}
			placement='bottom'
			align={{ offset: [-25] }}
			title='Customize Columns'
			trigger='click'
			content={getContent()}
			onOpenChange={setIsOpen}
		>
			<Tooltip title='Customize Columns'>
				<Button color='default' icon={<AlignHorizontalCenterTwo />} data-testid='ag-column-customize-btn' />
			</Tooltip>
		</Popover>
	);
};

export default ColumnPersonalization;
